import { useState, useCallback } from "react";
import { apiFetch } from "@shared/lib/api";
import { useAuthActions } from "@shared/lib/auth";
import { TOUR_STEPS } from "../components/onboarding/tourSteps";
import { MOBILE_TOUR_STEPS } from "../components/onboarding/mobileTourSteps";
import type { TourStepDef } from "../components/onboarding/tourSteps";

type Platform = "desktop" | "mobile";

type PlatformConfig = {
  steps: TourStepDef[];
  completeEndpoint: string;
};

const PLATFORM_CONFIG: Record<Platform, PlatformConfig> = {
  desktop: {
    steps: TOUR_STEPS,
    completeEndpoint: "/api/v1/me/complete_tour",
  },
  mobile: {
    steps: MOBILE_TOUR_STEPS,
    completeEndpoint: "/api/v1/me/complete_mobile_tour",
  },
};

type UseOnboardingTourOptions = {
  platform?: Platform;
};

/**
 * Onboarding tour hook. Supports both desktop and mobile platforms,
 * which differ in their step list and completion endpoint.
 *
 * The `acknowledging` state and `acknowledge` callback are only
 * meaningful on platforms whose step list contains an acknowledgment
 * step (currently desktop only). They are still exposed on all
 * platforms so callers don't need a separate type, but they will be
 * no-ops if no step requires acknowledgment.
 */
export function useOnboardingTour({ platform = "desktop" }: UseOnboardingTourOptions = {}) {
  const { steps, completeEndpoint } = PLATFORM_CONFIG[platform];
  const [step, setStep] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const { refreshMe } = useAuthActions();

  const totalSteps = steps.length;
  const currentStep = steps[step];

  const next = useCallback(() => {
    setStep((s) => (s < totalSteps - 1 ? s + 1 : s));
  }, [totalSteps]);

  const back = useCallback(() => {
    setStep((s) => (s > 0 ? s - 1 : s));
  }, []);

  const acknowledge = useCallback(async () => {
    setAcknowledging(true);
    try {
      await apiFetch("/api/v1/me/acknowledge_data_use", { method: "PATCH" });
      await refreshMe();
      // Move to the next step after successful acknowledgment
      setStep((s) => (s < totalSteps - 1 ? s + 1 : s));
    } catch {
      // Will retry on next click
    } finally {
      setAcknowledging(false);
    }
  }, [refreshMe, totalSteps]);

  const finish = useCallback(async () => {
    setIsActive(false);
    try {
      await apiFetch(completeEndpoint, { method: "PATCH" });
      await refreshMe();
    } catch {
      // refreshMe will pick up the updated user on next load
    }
  }, [refreshMe, completeEndpoint]);

  return {
    step,
    totalSteps,
    currentStep,
    isActive,
    acknowledging,
    next,
    back,
    acknowledge,
    finish,
    isFirstStep: step === 0,
    isLastStep: step === totalSteps - 1,
  };
}
